import React from "react";
import moment from "moment";
import { LuActivity, LuCalendar, LuGlobe, LuUser } from "react-icons/lu";

import Badge from "../ui/Badge";
import Button from "../ui/Button";
import { Dialog, DialogContent, DialogFooter, DialogHeader } from "../ui/Dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/Table";
import cx from "../ui/cx";
import {
  cardClass,
  cardContentFlushClass,
  dialogMobileBodyClass,
  dialogMobileContentClass,
  dialogMobileFooterClass,
  dialogMobileFormClass,
  dialogMobileHeaderClass,
  dialogTitleClass,
  tableHeadCellClass,
} from "../ui/styles";
import { ActivityLogProps } from "../../types/activityLog";

type Props = {
  log: ActivityLogProps;
  moduleLabel: string;
  onClose: () => void;
};

const formatDate = (value: string) =>
  moment(value).format("DD-MM-YYYY hh:mm A");

const actionBadge = (action: string) =>
  action === "update" ? (
    <Badge
      variant="none"
      className="bg-blue-100 text-blue-800 hover:bg-blue-100"
    >
      અપડેટ
    </Badge>
  ) : (
    <Badge variant="none" className="bg-red-100 text-red-800 hover:bg-red-100">
      ડિલીટ
    </Badge>
  );

const infoLabelClass = cx(tableHeadCellClass, "text-sm");

const infoIconClass = "h-4 w-4 mt-1 flex-shrink-0 text-gray-400";

const cardRowClass = "flex items-start justify-between gap-4";

const cardLabelClass = cx(tableHeadCellClass, "shrink-0");

const valueClass = "[overflow-wrap:anywhere]";

const hasValue = (value: unknown) =>
  value !== null && value !== undefined && value !== "";

const formatValue = (value: unknown) => {
  if (!hasValue(value)) return "-";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const renderValue = (
  key: string,
  value: unknown,
  references: Record<string, string> | null
) => {
  const reference = hasValue(value) ? references?.[key] : undefined;

  if (key === "image" && reference) {
    return (
      <a
        href={reference}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-block"
      >
        <img
          src={reference}
          alt={key}
          className="h-[100px] w-[100px] cursor-pointer rounded-md border object-cover hover:opacity-80"
        />
      </a>
    );
  }

  return reference
    ? `${formatValue(value)} (${reference})`
    : formatValue(value);
};

const ActivityLogDetailModal = ({ log, moduleLabel, onClose }: Props) => {
  const isDelete = log.action === "delete";
  const oldValues = log.old_values || {};
  const newValues = log.new_values || {};
  const oldReferences = isDelete ? log.references : null;
  const fields = Array.from(
    new Set([...Object.keys(oldValues), ...Object.keys(newValues)])
  );

  return (
    <Dialog open onClose={onClose}>
      <DialogContent
        size="wide"
        className={cx(
          "font-inter grid-rows-[auto_minmax(0,1fr)]",
          dialogMobileContentClass
        )}
      >
        <DialogHeader className={cx("pb-4 border-b", dialogMobileHeaderClass)}>
          <h2 className={dialogTitleClass}>એક્ટિવિટી લોગ ની વિગતો</h2>
          <p className="text-sm text-gray-500">
            {moduleLabel} · ID: {log.model_id}
          </p>
        </DialogHeader>

        <div
          className={cx(
            "flex min-h-0 flex-col space-y-4",
            dialogMobileFormClass
          )}
        >
          <div
            className={cx(
              "min-h-0 flex-1 space-y-4 overflow-y-auto",
              dialogMobileBodyClass
            )}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              <div className="flex items-start gap-2">
                <LuUser className={infoIconClass} />
                <div className="min-w-0">
                  <div className={infoLabelClass}>યુઝર</div>
                  <div className="font-medium break-words">
                    {log.user?.name || "-"}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <LuCalendar className={infoIconClass} />
                <div className="min-w-0">
                  <div className={infoLabelClass}>તારીખ</div>
                  <div className="font-medium">
                    {formatDate(log.created_at)}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <LuActivity className={infoIconClass} />
                <div className="min-w-0">
                  <div className={infoLabelClass}>એકશન</div>
                  <div>{actionBadge(log.action)}</div>
                </div>
              </div>

              <div className="flex items-start gap-2">
                <LuGlobe className={infoIconClass} />
                <div className="min-w-0">
                  <div className={infoLabelClass}>IP એડ્રેસ</div>
                  <div className="font-medium break-words">
                    {log.ip_address || "-"}
                  </div>
                </div>
              </div>
            </div>

            {isDelete && (
              <p className="text-sm text-gray-500">રેકોર્ડ ડિલીટ થયેલ છે</p>
            )}

            <div className={cx(cardClass, "max-sm:hidden")}>
              <div className={cardContentFlushClass}>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className={tableHeadCellClass}>
                          ફીલ્ડ
                        </TableHead>
                        <TableHead className={tableHeadCellClass}>
                          જૂની વિગતો
                        </TableHead>
                        {!isDelete && (
                          <TableHead className={tableHeadCellClass}>
                            નવી વિગતો
                          </TableHead>
                        )}
                      </TableRow>
                    </TableHeader>

                    <TableBody>
                      {fields.map((key) => (
                        <TableRow key={key}>
                          <TableCell className="whitespace-nowrap font-medium">
                            {key}
                          </TableCell>

                          <TableCell className={cx(valueClass, "text-red-800")}>
                            {renderValue(key, oldValues[key], oldReferences)}
                          </TableCell>

                          {!isDelete && (
                            <TableCell
                              className={cx(valueClass, "text-green-800")}
                            >
                              {renderValue(key, newValues[key], log.references)}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>

            <div className={cx(cardClass, "sm:hidden")}>
              <div className="space-y-3 p-4 text-sm">
                {fields.map((key) => (
                  <div key={key} className={cardRowClass}>
                    <span className={cardLabelClass}>{key}</span>
                    <div className={cx("min-w-0 text-right", valueClass)}>
                      {isDelete ? (
                        <div className="text-red-800">
                          {renderValue(key, oldValues[key], oldReferences)}
                        </div>
                      ) : (
                        <>
                          <div className="text-red-800">
                            જૂની: {renderValue(key, oldValues[key], oldReferences)}
                          </div>
                          <div className="text-green-800">
                            નવી: {renderValue(key, newValues[key], log.references)}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className={cx("justify-end", dialogMobileFooterClass)}>
            <Button
              type="button"
              variant="outline"
              className="max-sm:flex-1"
              onClick={onClose}
            >
              બંધ કરો
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ActivityLogDetailModal;
